import { useCallback, useEffect, useState } from "react";
import { contractSignatureStatus } from "@/lib/contract-status";
import {
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  ExternalLink,
  FileCheck,
  FileText,
  FileUp,
  Info,
  Loader2,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { EnvironmentBadge, PageHead, Panel } from "@/components/bits";
import { CountrySelect } from "@/components/country-select";
import { ApiError, publicApi } from "@/services/api-client";
import { useI18n } from "@/i18n/i18n-context";
import {
  Chip,
  Empty,
  ErrorBox,
  MetaList,
  Skeleton,
  errorMessage,
  list,
  rec,
  txt,
  type Rec,
} from "@/features/onboarding/shared";

/** The case may be returned at the root or nested under `case`. */
function caseOf(data: Rec): Rec {
  const nested = rec(data.case);
  return Object.keys(nested).length ? nested : data;
}

function requirementKey(r: Rec | string): string {
  return typeof r === "string"
    ? r
    : txt(r.category ?? r.code ?? r.key ?? r.name);
}

function arr(...vals: unknown[]): (Rec | string)[] {
  for (const v of vals)
    if (Array.isArray(v))
      return v.map((x) => (typeof x === "string" ? x : rec(x)));
  return [];
}

const isHttps = (u: string) => /^https:\/\//i.test(u);

export default function Verification() {
  const d = useDashboard();
  const { t, dict, lang } = useI18n();

  const getCategoryMeta = useCallback(
    (k: string) => {
      const upperKey = requirementKey(k).toUpperCase();
      const cat = (
        dict.verification.categories as Record<
          string,
          { label: string; desc: string }
        >
      )[upperKey];
      if (cat) {
        return {
          label: cat.label,
          description: cat.desc,
          accepted: "PDF, JPG, PNG, WEBP (max 10 Mo)",
        };
      }
      return null;
    },
    [dict],
  );

  const requirementLabel = useCallback(
    (r: Rec | string): string => {
      const key = requirementKey(r);
      const upperKey = key.toUpperCase();
      const meta = (
        dict.verification.categories as Record<
          string,
          { label: string; desc: string }
        >
      )[upperKey];
      if (meta) {
        return meta.label;
      }
      if (typeof r === "string") {
        return key
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");
      }
      return txt(r.label ?? r.name ?? r.title ?? r.category ?? r.code, key);
    },
    [dict],
  );

  const getStatusLabel = useCallback(
    (s: string) => {
      const upper = s.toUpperCase();
      return (dict.verification.statuses as Record<string, string>)[upper] ?? s;
    },
    [dict],
  );

  const getContractStatusLabel = useCallback(
    (s: string) => {
      const upper = s.toUpperCase();
      return (
        (dict.verification.contractStatuses as Record<string, string>)[upper] ??
        s
      );
    },
    [dict],
  );

  const translateRule = useCallback(
    (rule: string) => {
      const trimmed = rule.trim();
      const rulesDict = dict.verification.rules as Record<string, string>;
      return rulesDict[trimmed] ?? rulesDict[rule] ?? rule;
    },
    [dict],
  );

  const translateContractTitle = useCallback(
    (title: string) => {
      if (!title) return t("verification.contractTitle");
      if (
        /business.*agreement/i.test(title) ||
        /business.*contract/i.test(title) ||
        /service.*agreement/i.test(title)
      ) {
        return lang === "en"
          ? "BizConnectPay Merchant Service Agreement"
          : "Contrat d'agrégation marchande BizConnectPay";
      }
      if (
        /individual.*policy/i.test(title) ||
        /individual.*verification/i.test(title)
      ) {
        return lang === "en"
          ? "Individual Account Verification Policy"
          : "Politique de vérification de compte individuel";
      }
      return title;
    },
    [lang, t],
  );

  const [state, setState] = useState<{
    loading: boolean;
    error: string;
    notFound: boolean;
    data: Rec;
  }>({ loading: true, error: "", notFound: false, data: {} });
  const [docs, setDocs] = useState<{
    loading: boolean;
    error: string;
    items: Rec[];
  }>({ loading: false, error: "", items: [] });
  const [country, setCountry] = useState("CM");
  const [language, setLanguage] = useState(lang);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");

  const [selectedTier, setSelectedTier] = useState("");

  // Per-category slot files for batch upload
  const [slotFiles, setSlotFiles] = useState<Record<string, File | null>>({});
  const [batchProgress, setBatchProgress] = useState<{
    current: number;
    total: number;
    category: string;
  } | null>(null);

  const [docDetail, setDocDetail] = useState<{
    id: string;
    data: Rec | null;
    error: string;
  } | null>(null);
  const [contracts, setContracts] = useState<
    Record<string, { data?: Rec; error?: string; loading?: boolean }>
  >({});
  const [signedFiles, setSignedFiles] = useState<Record<string, File | null>>(
    {},
  );
  const [confirm, setConfirm] = useState(false);

  const kase = caseOf(state.data);
  const caseId = txt(kase.id);
  const policy = rec(state.data.policy);
  const verificationPolicy = rec(state.data.verification_policy);
  const policyTiers = rec(verificationPolicy.tiers);
  const caseData = rec(kase.data);
  const displayedTier = txt(
    caseData.verification_tier ?? state.data.selected_verification_tier,
    txt(verificationPolicy.current_tier),
  );

  const ownerType = txt(
    kase.type ?? kase.case_type ?? kase.owner_type,
    d.isBusiness ? "KYB" : "KYC",
  )
    .toUpperCase()
    .includes("KYB")
    ? "KYB"
    : "KYC";

  const status = txt(kase.status, "NOT_STARTED").toUpperCase();

  // Combine required documents from policy, case.data, or root
  const requiredList = arr(
    state.data.required_documents,
    policy.required_documents,
    caseData.required_documents,
    kase.required_documents,
    state.data.requirements,
    kase.requirements,
  );
  const acceptedIdentityCategories = arr(
    caseData.accepted_identity_document_categories,
    state.data.accepted_identity_document_categories,
    d.isBusiness
      ? ["NATIONAL_ID", "CNI", "PASSPORT", "DRIVER_LICENSE"]
      : [
          "IDENTITY_RECEIPT",
          "RECEIPT",
          "ELECTION_CARD",
          "VOTER_CARD",
          "NATIONAL_ID",
          "CNI",
          "PASSPORT",
        ],
  ).map(requirementKey);

  const rulesList: string[] = arr(
    policy.rules,
    caseData.rules,
    state.data.rules,
  ).map((r) => txt(r));

  // Map of category -> active uploaded document
  const uploadedCategoryMap = new Map<string, Rec>();
  for (const doc of docs.items) {
    const cat = txt(doc.category).toUpperCase();
    if (cat && (!uploadedCategoryMap.has(cat) || doc.status === "ACTIVE")) {
      uploadedCategoryMap.set(cat, doc);
    }
  }

  const uploadedForRequirement = (key: string): Rec | undefined => {
    if (key.toUpperCase() === "IDENTITY_DOCUMENT") {
      const canonicalDocument = uploadedCategoryMap.get("IDENTITY_DOCUMENT");
      if (canonicalDocument) return canonicalDocument;

      return acceptedIdentityCategories
        .map((accepted) => uploadedCategoryMap.get(accepted.toUpperCase()))
        .find(Boolean);
    }

    return uploadedCategoryMap.get(key.toUpperCase());
  };

  // Set of missing requirement keys
  const missingKeys = new Set<string>();
  for (const r of requiredList) {
    const key = requirementKey(r);
    if (key && !uploadedForRequirement(key)) {
      missingKeys.add(key);
    }
  }

  const missingList = requiredList.filter((r) =>
    missingKeys.has(requirementKey(r)),
  );

  const contractList = arr(
    state.data.contracts,
    kase.contracts,
    state.data.contract ? [state.data.contract] : [],
  ).map((c) => (typeof c === "string" ? { id: c } : c));

  const editable = ![
    "SUBMITTED",
    "IN_REVIEW",
    "UNDER_REVIEW",
    "APPROVED",
  ].includes(status);

  const loadDocs = useCallback(async (id: string, type: string) => {
    setDocs((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await publicApi<unknown>(
        `/compliance-documents?owner_type=${type}&owner_id=${encodeURIComponent(id)}`,
      );
      setDocs({
        loading: false,
        error: "",
        items: list(r.data, ["documents"]),
      });
    } catch (e) {
      setDocs({ loading: false, error: errorMessage(e), items: [] });
    }
  }, []);

  const loadCase = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await publicApi<Rec>("/compliance/cases/current", {
        cache: "no-store",
      });
      const data = rec(r.data);
      setState({ loading: false, error: "", notFound: false, data });
      const k = caseOf(data);
      const selected = txt(
        rec(k.data).verification_tier ?? data.selected_verification_tier,
      );
      if (
        ["SIMPLIFIED", "IDENTITY_VERIFIED", "ENTERPRISE"].includes(selected)
      ) {
        setSelectedTier(selected);
      }
      if (txt(k.id)) {
        const t = txt(
          k.type ?? k.case_type ?? k.owner_type,
          d.isBusiness ? "KYB" : "KYC",
        )
          .toUpperCase()
          .includes("KYB")
          ? "KYB"
          : "KYC";
        void loadDocs(txt(k.id), t);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 404)
        setState({ loading: false, error: "", notFound: true, data: {} });
      else
        setState({
          loading: false,
          error: errorMessage(e, "Impossible de charger le dossier."),
          notFound: false,
          data: {},
        });
    }
  }, [d.isBusiness, loadDocs]);

  useEffect(() => {
    void loadCase();
  }, [loadCase]);

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setActionError("");
    setActionSuccess("");
    try {
      await fn();
      await loadCase();
      void d.refresh();
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const createCase = () =>
    run("create", async () => {
      await publicApi("/compliance/cases", {
        method: "POST",
        body: {
          country_code: country,
          language,
          ...(d.isBusiness ? { verification_tier: selectedTier } : {}),
        },
      });
    });

  const uploadSlot = (cat: string) =>
    run(`slot-${cat}`, async () => {
      const selected = slotFiles[cat];
      if (!selected) throw new Error(t("verification.chooseFileFirst"));
      const fd = new FormData();
      fd.append("owner_type", ownerType);
      fd.append("owner_id", caseId);
      fd.append("category", cat);
      fd.append("file", selected);
      await publicApi("/compliance-documents", { method: "POST", body: fd });
      setSlotFiles((prev) => ({ ...prev, [cat]: null }));
      setActionSuccess(t("verification.uploadSuccess", { category: cat }));
    });

  const uploadAllSelected = async () => {
    const toUpload = Object.entries(slotFiles).filter(
      ([, f]) => f !== null,
    ) as [string, File][];

    if (toUpload.length === 0) return;

    setBusy("upload-all");
    setActionError("");
    setActionSuccess("");
    setBatchProgress({
      current: 0,
      total: toUpload.length,
      category: toUpload[0][0],
    });

    try {
      for (let i = 0; i < toUpload.length; i++) {
        const [cat, f] = toUpload[i];
        setBatchProgress({
          current: i + 1,
          total: toUpload.length,
          category: cat,
        });

        const fd = new FormData();
        fd.append("owner_type", ownerType);
        fd.append("owner_id", caseId);
        fd.append("category", cat);
        fd.append("file", f);

        await publicApi("/compliance-documents", { method: "POST", body: fd });
        setSlotFiles((prev) => ({ ...prev, [cat]: null }));
      }

      setActionSuccess(
        t("verification.batchSuccess", { count: toUpload.length }),
      );
      await loadDocs(caseId, ownerType);
      await loadCase();
      void d.refresh();
    } catch (e) {
      setActionError(errorMessage(e, t("verification.batchError")));
      void loadDocs(caseId, ownerType);
    } finally {
      setBusy(null);
      setBatchProgress(null);
    }
  };

  const openDoc = async (id: string) => {
    setDocDetail({ id, data: null, error: "" });
    try {
      const r = await publicApi<Rec>(
        `/compliance-documents/${encodeURIComponent(id)}`,
        { cache: "no-store" },
      );
      setDocDetail({
        id,
        data: rec(rec(r.data).document ?? r.data),
        error: "",
      });
    } catch (e) {
      setDocDetail({ id, data: null, error: errorMessage(e) });
    }
  };

  const loadContract = async (id: string) => {
    setContracts((c) => ({ ...c, [id]: { loading: true } }));
    try {
      const r = await publicApi<Rec>(
        `/compliance/contracts/${encodeURIComponent(id)}`,
        { cache: "no-store" },
      );
      setContracts((c) => ({
        ...c,
        [id]: { data: rec(rec(r.data).contract ?? r.data) },
      }));
    } catch (e) {
      setContracts((c) => ({ ...c, [id]: { error: errorMessage(e) } }));
    }
  };

  const uploadSigned = (id: string) =>
    run(`signed-${id}`, async () => {
      const f = signedFiles[id];
      if (!f) throw new Error(t("verification.chooseFileFirst"));
      const fd = new FormData();
      fd.append("file", f);
      fd.append("document", f);
      await publicApi(
        `/compliance/contracts/${encodeURIComponent(id)}/signed-document`,
        { method: "POST", body: fd },
      );
      setSignedFiles((s) => ({ ...s, [id]: null }));
      await loadContract(id);
      setActionSuccess(t("verification.signedUploadSuccess"));
    });

  const submit = () =>
    run("submit", async () => {
      await publicApi(
        `/compliance/cases/${encodeURIComponent(caseId)}/submit`,
        { method: "POST", body: {} },
      );
      setConfirm(false);
      setActionSuccess(t("verification.submitSuccess"));
    });

  const doneCount = requiredList.length - missingKeys.size;
  const selectedBatchCount = Object.values(slotFiles).filter(Boolean).length;
  const allRequiredUploaded = requiredList.length > 0 && missingKeys.size === 0;

  return (
    <>
      <PageHead
        title={t("verification.title")}
        sub={d.isBusiness ? t("verification.subKyb") : t("verification.subKyc")}
      >
        <EnvironmentBadge />
      </PageHead>

      {actionError && (
        <div className="mb-4">
          <ErrorBox message={actionError} testId="error-verification-action" />
        </div>
      )}

      <Panel
        title={
          lang === "en"
            ? "Verification levels and limits"
            : "Niveaux de vérification et plafonds"
        }
        sub={
          !d.isBusiness
            ? lang === "en"
              ? "Individual accounts follow the existing KYC process and do not have API access."
              : "Les comptes individuels suivent le parcours KYC existant et ne disposent pas d’accès à l’API."
            : lang === "en"
              ? "Payins and payouts share one daily limit per country, in that country's default currency. Payment-link payins are included; virtual-card operations are excluded."
              : "Les payins et payouts partagent un plafond journalier par pays, dans la devise locale de ce pays. Les payins par lien sont inclus ; les opérations de carte virtuelle sont exclues."
        }
      >
        {d.isBusiness ? (
          <div className="grid gap-3 md:grid-cols-3">
            {["SIMPLIFIED", "IDENTITY_VERIFIED", "ENTERPRISE"].map((tier) => {
              const limits = rec(policyTiers[tier]);
              const unlimited = limits.daily_shared_amount == null;
              const limit = unlimited
                ? lang === "en"
                  ? "Unlimited"
                  : "Illimité"
                : String(
                    limits.daily_shared_amount ??
                      (tier === "SIMPLIFIED" ? "30000" : "500000"),
                  );
              const countries =
                limits.max_countries == null
                  ? lang === "en"
                    ? "all available"
                    : "tous les pays disponibles"
                  : String(
                      limits.max_countries ?? (tier === "SIMPLIFIED" ? 5 : 8),
                    );
              return (
                <div
                  key={tier}
                  className={`rounded-lg border p-3 ${displayedTier === tier ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <p className="text-sm font-semibold">
                    {tier === "SIMPLIFIED"
                      ? lang === "en"
                        ? "Simplified"
                        : "Simplifiée"
                      : tier === "IDENTITY_VERIFIED"
                        ? lang === "en"
                          ? "Identity verified"
                          : "Identité vérifiée"
                        : lang === "en"
                          ? "Enterprise"
                          : "Entreprise"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {lang === "en"
                      ? `Daily shared cap: ${limit}${unlimited ? "" : " per country"} · ${countries} ${limits.max_countries == null ? "" : "countries max"}`
                      : `Plafond journalier partagé : ${limit}${unlimited ? "" : " par pays"} · ${countries} ${limits.max_countries == null ? "" : "pays maximum"}`}
                  </p>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
            {lang === "en"
              ? "Individual accounts follow the existing KYC rules. Accepted identity documents: receipt, voter card, national ID or passport, with a selfie holding the ID and proof of address. API access and the business KYB limits are not available to individual accounts."
              : "Les comptes individuels suivent le parcours KYC existant. Pièces acceptées : récépissé, carte d’électeur, CNI ou passeport, avec un selfie tenant la pièce et un justificatif de domicile. L’accès API et les plafonds KYB des entreprises ne sont pas disponibles pour les comptes individuels."}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {d.isBusiness
            ? lang === "en"
              ? "Enterprise KYB requires five documents: representative identity, selfie with ID, proof of address, business registration, and tax certificate. No registered-office proof is requested."
              : "Le KYB entreprise requiert cinq documents : identité du représentant, selfie avec pièce d’identité, justificatif de domicile, immatriculation de l’entreprise et attestation fiscale. Aucun justificatif de siège social n’est demandé."
            : lang === "en"
              ? "KYC requirements for individuals are distinct from business KYB tiers. API access remains unavailable."
              : "Les exigences KYC des particuliers sont distinctes des niveaux KYB des entreprises. L’accès API reste indisponible."}
        </p>
      </Panel>

      {actionSuccess && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
          <span>{actionSuccess}</span>
        </div>
      )}

      {state.loading && !caseId ? (
        <Skeleton rows={4} />
      ) : state.error ? (
        <ErrorBox message={state.error} onRetry={() => void loadCase()} />
      ) : state.notFound || !caseId ? (
        <Panel
          title={t("verification.openCaseTitle")}
          sub={t("verification.openCaseSub", {
            type: d.isBusiness ? "KYB" : "KYC",
          })}
        >
          <div className="grid w-full gap-5" data-testid="form-create-case">
            {d.isBusiness && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {lang === "en"
                    ? "Choose one verification level before continuing."
                    : "Choisissez un seul niveau de vérification avant de continuer."}
                </p>
                <div
                  className="grid gap-3 md:grid-cols-3"
                  role="radiogroup"
                  aria-label={
                    lang === "en"
                      ? "Verification level"
                      : "Niveau de vérification"
                  }
                >
                  {(
                    ["SIMPLIFIED", "IDENTITY_VERIFIED", "ENTERPRISE"] as const
                  ).map((tier) => {
                    const limits = rec(policyTiers[tier]);
                    const active = selectedTier === tier;
                    const amount =
                      tier === "ENTERPRISE"
                        ? "∞"
                        : String(
                            limits.daily_shared_amount ??
                              (tier === "SIMPLIFIED" ? "30000" : "500000"),
                          );
                    const maxCountries =
                      tier === "ENTERPRISE"
                        ? lang === "en"
                          ? "all"
                          : "tous"
                        : String(
                            limits.max_countries ??
                              (tier === "SIMPLIFIED" ? 5 : 8),
                          );
                    const title =
                      tier === "SIMPLIFIED"
                        ? lang === "en"
                          ? "Simplified verification"
                          : "Vérification simplifiée"
                        : tier === "IDENTITY_VERIFIED"
                          ? lang === "en"
                            ? "Identity verified"
                            : "Identité vérifiée"
                          : lang === "en"
                            ? "Enterprise"
                            : "Entreprise";
                    const documents =
                      tier === "SIMPLIFIED"
                        ? lang === "en"
                          ? "Receipt or voter card, selfie holding the ID, and proof of address."
                          : "Récépissé ou carte d’électeur, selfie tenant la pièce et justificatif de domicile."
                        : tier === "IDENTITY_VERIFIED" && lang === "en"
                          ? "National ID, passport, or driver's license, selfie holding the ID, and proof of address."
                          : tier === "IDENTITY_VERIFIED"
                            ? "CNI, passeport ou permis de conduire, selfie tenant la pièce et justificatif de domicile."
                            : lang === "en"
                              ? "Representative ID, selfie with ID, proof of address, business registration, and tax certificate."
                              : "Identité du représentant, selfie avec pièce, justificatif de domicile, immatriculation et attestation fiscale.";
                    return (
                      <button
                        key={tier}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setSelectedTier(tier)}
                        className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:bg-muted/40"}`}
                      >
                        <span className="block text-sm font-semibold">
                          {title}
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {documents}
                        </span>
                        <span className="mt-2 block text-xs font-medium">
                          {lang === "en"
                            ? tier === "ENTERPRISE"
                              ? "Unlimited shared payin and payout · All enabled countries"
                              : `Shared payin + payout cap: ${amount} per day, per country · Up to ${maxCountries} countries`
                            : tier === "ENTERPRISE"
                              ? "Payin et payout partagés illimités · Tous les pays activés"
                              : `Plafond payin + payout partagé : ${amount} par jour et par pays · ${maxCountries} pays maximum`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!d.isBusiness && (
              <div className="rounded-xl border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                {lang === "en"
                  ? "KYC for individual accounts requires one accepted identity document (receipt, voter card, national ID, or passport), a selfie holding the ID, and proof of address. Individual accounts do not have API access."
                  : "Le KYC d’un compte individuel demande une pièce acceptée (récépissé, carte d’électeur, CNI ou passeport), un selfie tenant cette pièce et un justificatif de domicile. Les comptes individuels n’ont pas accès à l’API."}
              </div>
            )}
            <div className="grid max-w-md gap-1.5">
              <Label htmlFor="case-country">{t("common.country")}</Label>
              <CountrySelect
                id="case-country"
                value={country}
                onValueChange={setCountry}
                testId="select-case-country"
              />
            </div>
            <div className="grid max-w-md gap-1.5">
              <Label>{t("verification.caseLanguage")}</Label>
              <Select
                value={language}
                onValueChange={(val) => setLanguage(val === "en" ? "en" : "fr")}
              >
                <SelectTrigger data-testid="select-case-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="button-create-case"
              className="justify-self-start"
              disabled={busy === "create" || (d.isBusiness && !selectedTier)}
              onClick={() => void createCase()}
            >
              {busy === "create" && (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              {t("verification.createCaseButton")}
            </Button>
          </div>
        </Panel>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
          <div className="space-y-5">
            {/* Règles de conformité */}
            {rulesList.length > 0 && (
              <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-blue-950">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  {t("verification.rulesTitle")}
                </div>
                <ul className="mt-2.5 space-y-1.5 text-xs text-blue-900">
                  {rulesList.map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="font-bold">•</span>
                      <span>{translateRule(rule)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Checklist des exigences et téléversement en lot */}
            <Panel
              title={t("verification.requiredDocsTitle")}
              sub={
                requiredList.length
                  ? t("verification.doneOf", {
                      done: doneCount,
                      total: requiredList.length,
                    })
                  : t("verification.serverList")
              }
            >
              {requiredList.length > 0 && (
                <div className="mb-5 h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{
                      width: `${(doneCount / requiredList.length) * 100}%`,
                    }}
                  />
                </div>
              )}

              {/* Barre d'action téléverser tout si plusieurs fichiers sont prêts */}
              {editable && selectedBatchCount > 0 && (
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center gap-2.5">
                    <FileUp className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-bold text-foreground">
                        {t("verification.selectedCount", {
                          count: selectedBatchCount,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("verification.selectedBatchDesc")}
                      </p>
                    </div>
                  </div>
                  <Button
                    data-testid="button-upload-all-documents"
                    disabled={busy === "upload-all"}
                    onClick={() => void uploadAllSelected()}
                    className="gap-2"
                  >
                    {busy === "upload-all" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {busy === "upload-all" && batchProgress
                      ? t("verification.uploadingBatch", {
                          current: batchProgress.current,
                          total: batchProgress.total,
                        })
                      : t("verification.uploadAll", {
                          count: selectedBatchCount,
                        })}
                  </Button>
                </div>
              )}

              {requiredList.length === 0 ? (
                <Empty
                  title={t("verification.noRequirements")}
                  sub={t("verification.noRequirementsSub")}
                />
              ) : (
                <div className="space-y-3" data-testid="list-requirements">
                  {requiredList.map((r) => {
                    const key = requirementKey(r);
                    const uploaded = uploadedForRequirement(key);
                    const isDone = Boolean(uploaded);
                    const meta = getCategoryMeta(key);
                    const selectedForSlot = slotFiles[key];

                    return (
                      <div
                        key={key}
                        className={`rounded-xl border p-4 transition-all ${
                          isDone
                            ? "border-emerald-200 bg-emerald-50/20"
                            : selectedForSlot
                              ? "border-primary/40 bg-primary/5"
                              : "border-slate-200 bg-card"
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex items-start gap-3">
                            {isDone ? (
                              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                            ) : (
                              <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />
                            )}
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-foreground">
                                  {meta?.label ?? requirementLabel(r)}
                                </span>
                                <span className="font-mono text-[11px] text-muted-foreground">
                                  ({key})
                                </span>
                              </div>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {meta?.description ??
                                  (lang === "en"
                                    ? "Mandatory document required by compliance policy."
                                    : "Document obligatoire requis par la politique de conformité.")}
                              </p>
                              {meta?.accepted && (
                                <p className="mt-0.5 text-[11px] text-slate-400">
                                  Formats : {meta.accepted}
                                </p>
                              )}
                              {key.toUpperCase() === "IDENTITY_DOCUMENT" && (
                                <p className="mt-1 text-xs font-medium text-primary">
                                  {lang === "en"
                                    ? `Accepted: ${acceptedIdentityCategories.map(requirementLabel).join(", ")}`
                                    : `Acceptés : ${acceptedIdentityCategories.map(requirementLabel).join(", ")}`}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            {isDone ? (
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                {getStatusLabel("UPLOADED")}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                                <AlertCircle className="h-3.5 w-3.5" />
                                {lang === "en" ? "Required" : "Requis"}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Document déjà transmis */}
                        {isDone && uploaded && (
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2 text-xs">
                            <div className="flex items-center gap-2">
                              <FileCheck className="h-4 w-4 text-emerald-600" />
                              <span className="font-medium text-emerald-950">
                                {txt(
                                  uploaded.original_name,
                                  lang === "en"
                                    ? "Registered document"
                                    : "Document enregistré",
                                )}
                              </span>
                              <span className="text-emerald-700">
                                ({txt(uploaded.format, "").toUpperCase()})
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-emerald-800 hover:bg-emerald-100"
                              onClick={() => void openDoc(txt(uploaded.id))}
                            >
                              {t("verification.viewMetadata")}
                            </Button>
                          </div>
                        )}

                        {/* Téléversement pour ce créneau si modifiable */}
                        {editable && (!isDone || selectedForSlot) && (
                          <div className="mt-3 flex flex-wrap items-center gap-2 pt-2 border-t border-slate-100">
                            <div className="flex-1 min-w-[200px]">
                              <Input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg,.webp"
                                className="h-9 text-xs"
                                onChange={(e) => {
                                  const f = e.target.files?.[0] ?? null;
                                  setSlotFiles((prev) => ({
                                    ...prev,
                                    [key]: f,
                                  }));
                                }}
                              />
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={
                                !selectedForSlot || busy === `slot-${key}`
                              }
                              onClick={() => void uploadSlot(key)}
                              className="gap-1.5 text-xs"
                            >
                              {busy === `slot-${key}` ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Upload className="h-3.5 w-3.5" />
                              )}
                              {t("common.send")}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {missingList.length > 0 && (
                <p
                  className="mt-4 flex items-center gap-1.5 text-xs font-medium text-amber-700"
                  data-testid="text-missing"
                >
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {t("verification.missingDocs", {
                    list: missingList.map(requirementLabel).join(", "),
                  })}
                </p>
              )}
            </Panel>

            {docs.items.length > 0 && (
              <Panel
                title={
                  lang === "en" ? "Submitted documents" : "Documents transmis"
                }
                sub={
                  lang === "en"
                    ? "Documents attached to this verification case."
                    : "Pièces jointes à ce dossier de vérification."
                }
              >
                {docs.loading ? (
                  <Skeleton rows={2} />
                ) : docs.error ? (
                  <ErrorBox
                    message={docs.error}
                    onRetry={() => void loadDocs(caseId, ownerType)}
                  />
                ) : (
                  <ul className="space-y-2" data-testid="list-documents">
                    {docs.items.map((doc) => (
                      <li
                        key={txt(doc.id)}
                        className="flex flex-wrap items-center gap-3 rounded-lg border p-3 text-sm"
                      >
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <span className="font-medium">
                            {getCategoryMeta(txt(doc.category))?.label ??
                              txt(doc.category, "Document")}
                          </span>
                          <span className="ml-2 font-mono text-xs text-muted-foreground">
                            {txt(doc.original_name)}
                          </span>
                        </div>
                        <Chip
                          value={getStatusLabel(txt(doc.status, "UPLOADED"))}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`button-doc-detail-${txt(doc.id)}`}
                          onClick={() => void openDoc(txt(doc.id))}
                        >
                          {t("common.details")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Panel>
            )}

            {/* Contrat KYB si applicable */}
            {(ownerType === "KYB" || contractList.length > 0) && (
              <Panel
                title={t("verification.contractTitle")}
                sub={t("verification.contractSub")}
              >
                {contractList.length === 0 ? (
                  <Empty
                    title={t("verification.noContract")}
                    sub={t("verification.noContractSub")}
                  />
                ) : (
                  <ul className="space-y-3">
                    {contractList.map((c) => {
                      const id = txt(c.id ?? c.contract_id);
                      const st = contracts[id];
                      const url = txt(
                        rec(st?.data).download_url ??
                          rec(st?.data).url ??
                          rec(rec(st?.data).retrieval).url,
                      );
                      const sigStatus = contractSignatureStatus(
                        c,
                        rec(st?.data),
                      );
                      return (
                        <li
                          key={id}
                          className="space-y-3 rounded-lg border p-4"
                          data-testid={`contract-${id}`}
                        >
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="flex-1 text-sm font-semibold">
                              {translateContractTitle(
                                txt(c.title ?? c.name, ""),
                              )}
                            </span>
                            <Chip value={getContractStatusLabel(sigStatus)} />
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`button-contract-load-${id}`}
                              onClick={() => void loadContract(id)}
                              disabled={st?.loading}
                            >
                              {st?.loading && (
                                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                              )}
                              {t("verification.consultContract")}
                            </Button>
                          </div>
                          {st?.error && (
                            <ErrorBox
                              message={st.error}
                              onRetry={() => void loadContract(id)}
                            />
                          )}
                          {st?.data && (
                            <div className="space-y-2">
                              <MetaList
                                data={st.data}
                                omit={["url", "download_url"]}
                              />
                              {url && isHttps(url) && (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                                >
                                  {t("verification.downloadContract")}{" "}
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </div>
                          )}
                          {editable && (
                            <div className="flex flex-wrap items-end gap-2">
                              <Input
                                type="file"
                                className="max-w-xs"
                                data-testid={`input-signed-${id}`}
                                onChange={(e) =>
                                  setSignedFiles((s) => ({
                                    ...s,
                                    [id]: e.target.files?.[0] ?? null,
                                  }))
                                }
                              />
                              <Button
                                size="sm"
                                data-testid={`button-signed-upload-${id}`}
                                disabled={
                                  !signedFiles[id] || busy === `signed-${id}`
                                }
                                onClick={() => void uploadSigned(id)}
                              >
                                {busy === `signed-${id}` && (
                                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                )}
                                {t("verification.uploadSigned")}
                              </Button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Panel>
            )}
          </div>

          {/* Colonne latérale : statut et soumission */}
          <div className="space-y-5">
            <Panel title={t("verification.caseStatusTitle")}>
              <div className="flex items-center gap-3">
                <Chip
                  value={getStatusLabel(status)}
                  testId="text-verif-status"
                />
                <span className="text-xs text-muted-foreground font-semibold">
                  {ownerType} ·{" "}
                  {d.isBusiness
                    ? t("verification.businessAccount")
                    : t("verification.individualAccount")}
                </span>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                {status === "APPROVED"
                  ? t("verification.statusApproved")
                  : ["SUBMITTED", "IN_REVIEW", "UNDER_REVIEW"].includes(status)
                    ? t("verification.statusInReview")
                    : t("verification.statusPending")}
              </p>
              {d.isBusiness && d.locked && (
                <p className="mt-2.5 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs font-semibold text-amber-800">
                  {t("verification.lockedWarning")}
                </p>
              )}
            </Panel>

            <Panel title={t("verification.finalSubmission")}>
              {allRequiredUploaded ? (
                <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
                  <div className="flex items-center gap-1.5 font-bold text-emerald-800">
                    <CheckCircle2 className="h-4 w-4" />
                    {t("verification.completeTitle")}
                  </div>
                  <p className="mt-1">{t("verification.completeDesc")}</p>
                </div>
              ) : (
                <p className="mb-3 flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{t("verification.incompleteDesc")}</span>
                </p>
              )}

              <Button
                className="w-full gap-2"
                data-testid="button-kyb-submit"
                disabled={!editable || !allRequiredUploaded}
                onClick={() => setConfirm(true)}
              >
                {editable ? (
                  <>
                    <FileText className="h-4 w-4" />
                    {t("verification.submitButton")}
                  </>
                ) : (
                  <>
                    <Clock className="h-4 w-4" />
                    {t("verification.alreadySubmitted")}
                  </>
                )}
              </Button>
            </Panel>
          </div>
        </div>
      )}

      {/* Confirmation de soumission */}
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("verification.confirmTitle", { type: ownerType })}
            </DialogTitle>
            <DialogDescription>
              {t("verification.confirmDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="button-kyb-confirm"
              disabled={busy === "submit"}
              onClick={() => void submit()}
            >
              {busy === "submit" && (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              {t("verification.confirmButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Détails du document */}
      <Dialog open={!!docDetail} onOpenChange={(o) => !o && setDocDetail(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("verification.metadataTitle")}</DialogTitle>
          </DialogHeader>
          {docDetail?.error ? (
            <ErrorBox
              message={docDetail.error}
              onRetry={() => void openDoc(docDetail.id)}
            />
          ) : docDetail?.data ? (
            <MetaList data={docDetail.data} omit={["url", "secure_url"]} />
          ) : (
            <Skeleton rows={2} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
